import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import SlidingPanel, {
  PanelContent,
  runHideAnimation,
  ANIMATION_DURATION,
} from "components/SlidingPanel";
import {
  hideAddToDictPanel,
  setDictionaryError,
  setNewlyCreatedCategoryName,
  setNewlyCreatedDictionaryId,
  setSelectedCategoryName,
  setSelectedDictionaryId,
  setStep,
} from "slices/discovery/discoverySlice";
import { selectAddToDictPanel, selectConfig } from "slices/discovery/selectors";
import TwoOptionsRow from "components/TwoOptionsRow";
import VTButton, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import {
  Alert,
  alertTypes,
  alertVariants,
} from "@visiblethread/react-components/dist/components/Alert";
import SelectDictTable from "components/SelectDictTable";
import { sortingKeys } from "constants/dictionary";
import {
  addTerms,
  fetchDictionary,
  getDictionaries,
  unlockDictionary,
} from "slices/discovery/actions/actionButtons";
import CategoriesTable from "layout/discovery/CategoriesTable";
import { ReactComponent as BackIcon } from "icons/left_arrow_purple.svg";
import { showModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import { getFromSS } from "@visiblethread/react-components/dist/utils";

export const TERM_ALREADY_EXISTS_BANNER_ERROR =
  "This item already exists in this category";
export const TERM_ALREADY_EXISTS_TABLE_ERROR =
  "Item already exists in this category";

const AddToDictPanel = () => {
  const dispatch = useDispatch();
  const panelRef = useRef();

  const { folderId } = useSelector(selectConfig);
  const {
    terms,
    term,
    dictionaries,
    dictionary,
    selectedDictionaryId,
    step,
    dictionaryError,
    newlyCreatedDictionaryId,
    selectedCategoryName,
    newlyCreatedCategoryName,
  } = useSelector(selectAddToDictPanel);
  const singleTerm = terms.length === 1;
  const [addSelectedTermOnly, setAddSelectedTermOnly] = useState(false);
  const [selectedTermIndex, setSelectedTermIndex] = useState(null);
  const [bannerMessage, setBannerMessage] = useState(null);
  const [categoriesErrors, setCategoriesErrors] = useState(new Map());

  const hidePanel = () => {
    runHideAnimation(panelRef);

    // Wait for animation to finish and then close popup
    setTimeout(() => {
      dispatch(unlockDictionary());
      dispatch(hideAddToDictPanel());
    }, ANIMATION_DURATION);
  };

  const getTermsList = () => {
    let termsList = "";

    if (terms.length === 2) {
      termsList += ` "${terms[0]}"`;
      termsList += ` and "${terms[1]}".`;
    } else {
      termsList += ` "${terms[0]}",`;
      termsList += ` "${terms[1]}"`;
      termsList += ` and ${terms.length - 2} ${terms.length - 2 === 1 ? "other" : "others"}.`;
    }

    return termsList;
  };

  const headerConfig = [
    {
      title: `Dictionaries (${dictionaries.length})`,
      sortBy: sortingKeys.name,
    },
    {
      title: "Created By",
      sortBy: sortingKeys.createdBy,
    },

    {
      title: "Last Updated",
      sortBy: sortingKeys.lastUpdated,
    },
  ];

  const handleDictionarySelect = (id) => {
    dispatch(setSelectedDictionaryId(id));

    if (id !== selectedDictionaryId) {
      dispatch(setDictionaryError(null));
    }
  };

  const clearCategoryError = (categoryName) => {
    if (categoryName !== selectedCategoryName) {
      setBannerMessage(null);
      setCategoriesErrors(new Map());
    }
  };

  const handleCategorySelect = (categoryName) => {
    dispatch(setSelectedCategoryName(categoryName));
    setSelectedTermIndex(null);
    clearCategoryError(categoryName);
  };

  const handleTermSelect = (termIndex, categoryName) => {
    setSelectedTermIndex(termIndex);
    dispatch(setSelectedCategoryName(categoryName));
    clearCategoryError(categoryName);
  };

  const onDictionaryPick = (id) => {
    dispatch(fetchDictionary(id));
  };

  const handleNext = () => {
    onDictionaryPick(selectedDictionaryId);
  };

  const onCategoryPick = (categotyName, termIndex) => {
    let termsToAdd = addSelectedTermOnly ? [term] : terms;
    termsToAdd = termsToAdd.map((termName) => ({ name: termName }));
    const selectedCategory = dictionary.categories.find(
      (category) => category.name === categotyName
    );

    if (!selectedCategory) return;

    const existedTerms = selectedCategory.terms;

    const newTerms = termsToAdd.filter(
      (termToAdd) => !existedTerms.find(({ name }) => name === termToAdd.name)
    );

    if (newTerms.length === 0) {
      setBannerMessage(TERM_ALREADY_EXISTS_BANNER_ERROR);
      const newCategoriesErrors = new Map(categoriesErrors);
      newCategoriesErrors.set(categotyName, TERM_ALREADY_EXISTS_TABLE_ERROR);
      setCategoriesErrors(newCategoriesErrors);
      return;
    }

    const numberOfRepeatedTerms = termsToAdd.length - newTerms.length;

    dispatch(
      addTerms(newTerms, categotyName, termIndex, numberOfRepeatedTerms)
    );
  };

  const handleAdd = () => {
    onCategoryPick(selectedCategoryName, selectedTermIndex);
  };

  const handleCancel = () => {
    hidePanel();
  };

  const handleBackClick = () => {
    dispatch(unlockDictionary());
    dispatch(setStep(1));
    dispatch(setSelectedCategoryName(null));
    setSelectedTermIndex(null);
    dispatch(setSelectedDictionaryId(null));
    sessionStorage.removeItem("add-to-dict-panel");
  };

  const handleCreateNewDictionary = () => {
    dispatch(showModal(modalTypes.CREATE_NEW_DICTIONARY));
  };

  const handleCreateNewCategory = () => {
    let insertAt;

    if (selectedCategoryName) {
      const selectedCategoryIndex = dictionary.categories.findIndex(
        (category) => category.name === selectedCategoryName
      );

      insertAt = selectedCategoryIndex + 1;
    } else {
      insertAt = 0;
    }

    dispatch(showModal(modalTypes.CREATE_NEW_CATEGORY, { insertAt }));
  };

  useEffect(() => {
    dispatch(getDictionaries());
  }, [dispatch]);

  useEffect(() => {
    setBannerMessage(null);
    setCategoriesErrors(new Map());
  }, [step]);

  useLayoutEffect(() => {
    try {
      const panelSettingsStringified = getFromSS("add-to-dict-panel");

      if (!panelSettingsStringified) {
        throw new Error("Panel settings: empty");
      }

      const panelSettings = JSON.parse(panelSettingsStringified);

      if (
        panelSettings.folderId === undefined ||
        panelSettings.dictionaryId === undefined
      ) {
        throw new Error("Panel settings: invalid format");
      }

      if (folderId !== panelSettings.folderId) {
        sessionStorage.removeItem("add-to-dict-panel");
        throw new Error("Panel settings: different folder");
      }

      dispatch(setStep(2));
      dispatch(fetchDictionary(panelSettings.dictionaryId));
    } catch (e) {}
  }, []);

  const getFirstStep = () => (
    <>
      <div>
        <b>{step}/2</b> Select dictionary below
      </div>
      {dictionaryError && (
        <>
          <hr className="add_to_dict_panel__divider" />
          <Alert
            type={alertTypes.inline}
            variant={alertVariants.warning}
            text={dictionaryError}
            hideCloseButton
          />
        </>
      )}
      {dictionaries.length && (
        <SelectDictTable
          dictionaries={dictionaries}
          selectedDictionaryId={selectedDictionaryId}
          newlyCreatedDictionaryId={newlyCreatedDictionaryId}
          resetNewlyCreatedDictionaryId={() =>
            dispatch(setNewlyCreatedDictionaryId(null))
          }
          headerConfig={headerConfig}
          initColumnWidths={[262, 86, 88]}
          minimalColumnWidths={[40, 40, 40]}
          onDictionarySelect={handleDictionarySelect}
          onEnter={handleNext}
          onDictionaryPick={onDictionaryPick}
        />
      )}
      <div className="add_to_dict_panel__create_dictionary_button">
        <VTButton
          content="+ Create New Dictionary"
          variant={buttonVariants.borderless}
          onClick={handleCreateNewDictionary}
        />
      </div>
    </>
  );

  const getSecondStep = () => (
    <>
      <div>
        <b>{step}/2</b> and select the category
      </div>
      <hr className="add_to_dict_panel__divider" />
      <div>
        {bannerMessage && (
          <Alert
            type={alertTypes.inline}
            variant={alertVariants.danger}
            text={bannerMessage}
            hideCloseButton
          />
        )}
        <div>
          <button
            type="button"
            className="add_to_dict_panel__back_button"
            onClick={handleBackClick}
          >
            <BackIcon />
          </button>
          <b>{dictionary.name}</b>
        </div>
      </div>
      <hr className="add_to_dict_panel__divider" />
      <CategoriesTable
        categories={dictionary.categories}
        selectedCategoryName={selectedCategoryName}
        newlyCreatedCategoryName={newlyCreatedCategoryName}
        resetNewlyCreatedCategoryName={() =>
          dispatch(setNewlyCreatedCategoryName(null))
        }
        categoriesErrors={categoriesErrors}
        onCategorySelect={handleCategorySelect}
        onTermSelect={handleTermSelect}
        onCategoryPick={onCategoryPick}
        onEnter={handleAdd}
      />
      <div className="add_to_dict_panel__create_category_button">
        <VTButton
          content="+ Create New Category"
          variant={buttonVariants.borderless}
          onClick={handleCreateNewCategory}
        />
      </div>
    </>
  );

  return (
    <SlidingPanel
      panelRef={panelRef}
      dataTestId="add-to-dict-panel"
      className="add_to_dict_panel"
      onDismiss={hidePanel}
    >
      <Modal.Header closeButton>Add to Dictionary</Modal.Header>
      <PanelContent>
        <Modal.Body>
          {singleTerm ? (
            <span>You are adding &quot;{terms[0]}&quot;</span>
          ) : (
            <>
              <p>
                You have checked {terms.length} items:{getTermsList()}
              </p>
              <TwoOptionsRow
                option1={`Add all ${terms.length} items`}
                option2={`Just add “${term}"`}
                state={addSelectedTermOnly}
                setState={setAddSelectedTermOnly}
              />
            </>
          )}
          <hr className="add_to_dict_panel__divider" />
          {step === 1 && getFirstStep()}
          {step === 2 && dictionary && getSecondStep()}
        </Modal.Body>
      </PanelContent>
      <Modal.Footer>
        <div>
          {step === 2 && selectedCategoryName !== null && (
            <>Adding to &quot;{selectedCategoryName}&quot;</>
          )}
        </div>
        {step === 1 && (
          <VTButton
            content="Next"
            variant={buttonVariants.primary}
            disabled={selectedDictionaryId === null || dictionaryError}
            onClick={handleNext}
          />
        )}
        {step === 2 && (
          <VTButton
            content="Add"
            variant={buttonVariants.primary}
            disabled={
              selectedCategoryName === null ||
              bannerMessage ||
              categoriesErrors.size
            }
            onClick={handleAdd}
          />
        )}
        <VTButton
          content="Cancel"
          variant={buttonVariants.secondary}
          disabled={false}
          onClick={handleCancel}
        />
      </Modal.Footer>
    </SlidingPanel>
  );
};

export default AddToDictPanel;
